/**
 * Deliberately a leaf module: every import here is type-only, so nothing in it
 * reaches a DLB-backed package at runtime.
 *
 * The early requester imports this, and early-requester scripts are inlined
 * above the DLB script tags -- pulling in anything that resolves to `react`,
 * `raven-js`, `hub-http`, `PortalIdParser` etc. fails the build with
 * "Early requester script depends on '<pkg>'". `useFetchViewsSearch` cannot be
 * imported from the early requester for exactly that reason, which is why the
 * shared request builder lives here rather than next to the hook.
 */

export const DEFAULT_PAGE_SIZE = 25;
export const COMPOSABLE_SEARCH_TIMEOUT_MS = 14000;

/**
 * Builds the composable-search request payload.
 *
 * Shared with the quick-fetch early request, which has to send a
 * *byte-identical* payload: `rpc-client-utils` keys early requests on a hash of
 * `{serviceName, rpcName, fingerprint, request}`, and the regular
 * `chirpClient.call` looks the response up under that same hash. One builder is
 * what keeps the two in step -- if they ever diverge the lookup simply misses
 * and the app fetches fresh, so a mismatch costs speed, never correctness.
 */
export const buildComposableSearchRequest = ({
  viewName,
  ownerIds,
  teamIds,
  objectTypeIds,
  private: isPrivate,
  limit,
  pagingToken,
  sortColumn,
  sortOrder,
  namespace,
  includeLastUsed,
  lastUsedStart,
  lastUsedEnd
}) => ({
  composableSearch: {
    namespace: namespace,
    objectTypeIds: objectTypeIds || [],
    // The v4 query type carries owner/team ids as strings; CHIRP expects ints.
    ownerIds: (ownerIds || []).map(Number),
    teamIds: (teamIds || []).map(Number),
    private: isPrivate,
    viewName,
    sortColumn: sortColumn || 'name',
    sortOrder: sortOrder || 'ASC',
    lastUsedTimestampFrom: lastUsedStart,
    lastUsedTimestampTo: lastUsedEnd
  },
  limit: limit || DEFAULT_PAGE_SIZE,
  /*
   * Normalised so the early request and the app's call serialise identically.
   * `usePagingState` starts at START_OF_PAGING, which it maps to '', while the
   * early requester has no paging state and would otherwise leave this
   * undefined -- and `JSON.stringify` drops undefined keys, so the two payloads
   * hashed differently and every prefetch missed. The wire value is unchanged:
   * the backend already treats '' as "no token".
   */
  pagingToken: pagingToken || '',
  includeLastUsed: Boolean(includeLastUsed)
});